import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../../shared/ui/Button';
import { TaskQuotaNotice } from '../components/TaskQuotaNotice';
import { useCreateSubtask } from '../hooks/useCreateSubtask';
import { useTaskSubtasks } from '../hooks/useTaskSubtasks';
import { useTasks } from '../hooks/useTasks';
import { getTaskLimit } from '../lib/quotaError';
import styles from './TaskDetailScreen.module.css';

interface TaskDetailScreenProps {
  taskId: number;
  onEditTask: (taskId: number) => void;
  onOpenBilling: () => void;
}

// PARA-105 §5 — дерево подзадач + форма добавления (карта экранов).
// PARA-122 (D2, AC-2): строки подзадач в стиле TaskCard (чекбокс-круг + title,
// done/pending), добавление — Button variant="dashed"; экранный --screen-pad-x.
// PARA-128 (AC-3a): read-only шапка задачи (title/size/priority) над списком.
// Семантика сохранена (ul/li, form input, submit) — контракт e2e не ломается.
// PARA-128 (AC-3b): значение размера берём из form.size.* (ед. ч.), а не из
// sections.* — те остаются заголовками секций бэклога (мн. ч., EC-6).
// PARA-131 (AC-15): создание подзадачи тоже проверяет квоту
// (split_task_into_subtask.py), поэтому 402 обрабатывается и здесь.
export function TaskDetailScreen({
  taskId,
  onEditTask,
  onOpenBilling,
}: TaskDetailScreenProps): JSX.Element {
  const { t } = useTranslation('tasks');
  const { data: tasks = [], isLoading: isTasksLoading } = useTasks();
  const { data: subtasks = [] } = useTaskSubtasks(taskId);
  const { mutate: createSubtask, isPending, error: createSubtaskError } = useCreateSubtask(taskId);
  const [title, setTitle] = useState('');

  const taskLimit = getTaskLimit(createSubtaskError);

  // PARA-128 (AC-3a) — собственные поля задачи берём из уже существующего кэша
  // useTasks() (тот же приём, что в TaskEditScreen; GET /tasks/{id} нет).
  // Инвалидация ['tasks', userId] в useUpdateTask обновляет шапку после
  // редактирования (EC-1); промах кэша → фолбэк notFound (EC-2).
  const task = tasks.find((candidate) => candidate.id === taskId);

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const trimmed = title.trim();
    if (trimmed.length === 0) {
      return;
    }
    createSubtask(trimmed, { onSuccess: () => setTitle('') });
  };

  // EC-7 — пока список задач грузится, «нет в кэше» ещё не значит «не найдена»:
  // нейтральное пустое состояние вместо вспышки notFound. Признак — isLoading,
  // а не isPending: у useTasks стоит enabled, и у отключённого запроса
  // isPending остаётся true навсегда.
  if (isTasksLoading) {
    return <div className={styles.screen} />;
  }

  // EC-2 / I3-5 — notFound здесь терминален (refetch на экране нет), поэтому
  // действия над несуществующей задачей не предлагаем вовсе: ни CTA, ни списка
  // подзадач, ни формы добавления.
  if (task === undefined) {
    return (
      <div className={styles.screen}>
        <p className={styles.notFound}>{t('form.errors.notFound')}</p>
      </div>
    );
  }

  return (
    <div className={styles.screen}>
      <div className={styles.summary}>
        <h2 className={styles.taskTitle}>{task.title}</h2>
        <dl className={styles.fields}>
          <div className={styles.field}>
            <dt className={styles.fieldLabel}>{t('form.sizeLabel')}</dt>
            <dd className={styles.fieldValue}>{t(`form.size.${task.size}`)}</dd>
          </div>
          <div className={styles.field}>
            <dt className={styles.fieldLabel}>{t('form.priorityLabel')}</dt>
            <dd className={styles.fieldValue}>{t(`form.priority.${task.priority}`)}</dd>
          </div>
        </dl>
      </div>
      <div className={styles.editAction}>
        <Button variant="secondary" fullWidth onClick={() => onEditTask(taskId)}>
          {t('editTask')}
        </Button>
      </div>
      <ul className={styles.list}>
        {subtasks.map((subtask) => {
          const done = subtask.status === 'DONE';
          return (
            <li key={subtask.id} className={styles.row}>
              <span className={done ? styles.checkboxDone : styles.checkbox} aria-hidden="true">
                {done ? '✓' : ''}
              </span>
              <span className={done ? `${styles.title} ${styles.titleDone}` : styles.title}>
                {subtask.title}
              </span>
            </li>
          );
        })}
      </ul>
      {taskLimit !== null && (
        <div className={styles.quota}>
          <TaskQuotaNotice taskLimit={taskLimit} onOpenBilling={onOpenBilling} />
        </div>
      )}
      <form className={styles.addForm} onSubmit={handleSubmit}>
        <input
          className={styles.input}
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          placeholder={t('addSubtask.placeholder')}
        />
        <Button type="submit" variant="dashed" fullWidth disabled={isPending}>
          + {t('addSubtask.submit')}
        </Button>
      </form>
    </div>
  );
}
